<template>
  <div
    class="i-mb-4 i-p-10 graph-container i-radius-xl"
    data-testid="credit-insights-daily-balance-graph"
  >
    <div
      v-if="dataLoading"
      class="d-flex align-items-center h-100 justify-content-center"
    >
      <i-spinner
        size="lg"
        type="neutral"
        subtype="subtle"
        data-testid="daily-balance-graph-container-spinner"
      />
    </div>
    <EmptyState
      v-else-if="emptyData"
      data-testid="daily-balance-graph-container-empty-state-section"
      title="No transaction data available"
      description="There is not enough transaction data to display this graph."
    />
    <div v-else data-testid="daily-balance-graph-container-graph-section">
      <div class="graph-header d-flex">
        <div class="graph-title d-flex flex-grow-1">
          <h6 class="i-text-heading-6">Daily Account Balance</h6>
        </div>
        <div class="graph-dropdown d-flex flex-shrink-0 i-p-2">
          <i-select
            class="account-selection-dropdown"
            placeholder="Select an account"
            name="account_selection"
            :options="accountSelectionOptions"
            :model-value="selectedAccount.id"
            @option-selected="changeSelectedAccount"
          ></i-select>
        </div>
        <div class="graph-menu d-flex flex-shrink-0 align-items-center i-pl-2">
          <i-menu :close-on-item-click="true" trigger-align="right">
            <template #trigger>
              <i-button
                left-icon="small-setting-menu-horizontal"
                :size="iButtonSizes.SMALL"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.GHOST"
              />
            </template>

            <template #items>
              <i-menu-item
                v-for="item in menuItems"
                :key="item.key"
                :label="item.label"
                v-read-only-check="{
                  alignment: 'right_middle',
                  active: item.readOnlyCheck,
                }"
                @item-click="selectMenuItem(item)"
              />
            </template>
          </i-menu>
        </div>
      </div>
      <div class="graph-chart">
        <DailyBalanceGraph
          :daily-balance-account-data="dailyBalanceAccountData"
          :selected-account="selectedAccount"
          :accounts-aggregate-data="accountsAggregateData"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  iSelect,
  iSpinner,
  iMenu,
  iMenuItem,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
} from '@inscribe/signal';
import DailyBalanceGraph from './DailyBalanceGraph.vue';
import EmptyState from '@/V2/credit/charts/EmptyState.vue';
import constants from './constants';
import { CHART_MENU_ITEMS } from '@/V2/credit/charts/charts.constants';
import { mapState } from 'vuex';
import moment from 'moment';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';

export default {
  name: 'DailyBalanceGraphContainer',
  components: {
    DailyBalanceGraph,
    EmptyState,
    iMenu,
    iMenuItem,
    iSelect,
    iSpinner,
    iButton,
  },
  data() {
    return {
      selectedAccount: constants.ALL_ACCOUNTS_OBJECT,
      menuItems: [...CHART_MENU_ITEMS],
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
      customerCashFlowBankAccounts: (state) =>
        state.credit?.customerCashflow?.bank_accounts,
      customerBankAccounts: (state) => state.customers?.customerBankAccounts,
    }),
    dataLoading() {
      return (
        this.credit?.customerCashflow === undefined ||
        this.customerBankAccounts === undefined
      );
    },
    emptyData() {
      if (this.dataLoading) {
        return true;
      } else {
        return this.customerBankAccounts.length === 0;
      }
    },
    accountSelectionOptions() {
      const accountDerivedOptions = [constants.ALL_ACCOUNTS_OBJECT];
      if (this.isAggregateAccountNeeded) {
        accountDerivedOptions.push(constants.AGGREGATE_ACCOUNTS_OBJECT);
      }
      accountDerivedOptions.push({
        id: 'divider1',
        label: '',
        divider: true,
      });
      const accountOptions = [
        ...accountDerivedOptions,
        ...this.dailyBalanceAccountData,
      ];
      return accountOptions.map(({ id, label, divider }) => ({
        value: id,
        text: label,
        divider: divider,
      }));
    },
    isAggregateAccountNeeded() {
      return (
        this.emptyData === false && this.dailyBalanceAccountData.length > 1
      );
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.params);
    },
    dailyBalanceAccountData() {
      if (this.dataLoading === false) {
        return this.getAccountData();
      }
      return [];
    },
    accountsMap() {
      if (this.emptyData) {
        return {};
      } else {
        const accountsMap = this.customerBankAccounts.reduce(
          (accountsMap, account) => {
            accountsMap[account.id] = account;
            return accountsMap;
          },
          {},
        );
        accountsMap[constants.ALL_ACCOUNTS_ID] = constants.ALL_ACCOUNTS_OBJECT;
        accountsMap[constants.AGGREGATE_ACCOUNTS_ID] =
          constants.AGGREGATE_ACCOUNTS_OBJECT;
        return accountsMap;
      }
    },
    accountsAggregateDataMap() {
      if (this.emptyData) {
        return undefined;
      } else {
        return this.getAccountsAggregateDataMap();
      }
    },
    accountsAggregateData() {
      const accountsAggregateData = this.defaultAccountsAggregateData();
      if (this.accountsAggregateDataMap !== undefined) {
        return this.setorderedAccountsAggregate(accountsAggregateData);
      }
      return accountsAggregateData;
    },
  },
  methods: {
    getAccountData() {
      const accounts = [];
      const bankAccounts = this.customerCashFlowBankAccounts;
      for (const [accountIndex, [id, value]] of Object.entries(
        Object.entries(bankAccounts),
      )) {
        if (this.bankAccountHasDailyBalanceData(id, bankAccounts) === false) {
          continue;
        }
        const daily_balances = bankAccounts[id].daily_balances;
        const daily_balance_dates = Object.keys(daily_balances);

        const data = [];
        for (const date of daily_balance_dates) {
          const balance = value.daily_balances[date];
          data.push({
            x: date,
            y: balance,
          });
        }
        const accountLabel = this.getAccountLabel(accountIndex, id);

        accounts.push({
          data: data,
          id: id,
          label: accountLabel,
        });
      }

      return accounts;
    },
    bankAccountHasDailyBalanceData(id, bankAccounts) {
      if (!this.accountsMap[id]) {
        return false;
      }
      const daily_balances = bankAccounts[id].daily_balances;
      if (daily_balances === null) {
        return false;
      }
      return true;
    },
    getAccountsAggregateDataMap() {
      const bankAccounts = this.customerCashFlowBankAccounts;
      const accountsAggregateDataMap = {};
      for (const id in bankAccounts) {
        const value = bankAccounts[id];
        if (this.bankAccountHasDailyBalanceData(id, bankAccounts) === false) {
          continue;
        }
        const daily_balances = bankAccounts[id].daily_balances;
        const daily_balance_dates = Object.keys(daily_balances);

        for (const date of daily_balance_dates) {
          const balance = value.daily_balances[date];
          this.addToAggregateAccount(accountsAggregateDataMap, date, balance);
        }
      }
      return accountsAggregateDataMap;
    },
    addToAggregateAccount(accountsAggregateDataMap, date, balance) {
      if (
        balance === null &&
        (accountsAggregateDataMap[date] === null ||
          typeof accountsAggregateDataMap[date] === 'undefined')
      ) {
        accountsAggregateDataMap[date] = null;
      } else {
        accountsAggregateDataMap[date] =
          (accountsAggregateDataMap[date] || 0) + balance;
      }
    },
    setorderedAccountsAggregate(accountsAggregateData) {
      const aggregateAccountDates = Object.keys(this.accountsAggregateDataMap);
      aggregateAccountDates.sort((a, b) => new Date(a) - new Date(b));

      for (let i = 0; i < aggregateAccountDates.length; i++) {
        const dateString = aggregateAccountDates[i];

        accountsAggregateData.data.push({
          x: dateString,
          y: this.accountsAggregateDataMap[dateString],
        });

        this.fillAnyDataTimeGapWithNans(
          accountsAggregateData,
          i,
          dateString,
          aggregateAccountDates,
        );
      }

      return accountsAggregateData;
    },
    // We need to fill any gaps in the data with NaNs as Chartjs does not
    // honour the spanGaps=false option when the axis is a time series.
    // This is described in this issue:
    // https://github.com/chartjs/Chart.js/issues/5222#issuecomment-485711191
    fillAnyDataTimeGapWithNans(
      accountsAggregateData,
      currentIndex,
      dateString,
      aggregateAccountDates,
    ) {
      if (currentIndex < aggregateAccountDates.length - 1) {
        const nextDateInDataSourceString =
          aggregateAccountDates[currentIndex + 1];
        const nextDateInDataSource = moment(
          nextDateInDataSourceString,
          'YYYY-MM-DD',
        );
        const currentIndexDate = moment(dateString, 'YYYY-MM-DD');
        let dateAfterCurrentIndex = moment(currentIndexDate).add(1, 'day');
        while (nextDateInDataSource > dateAfterCurrentIndex) {
          accountsAggregateData.data.push({
            x: dateAfterCurrentIndex.format('YYYY-MM-DD'),
            y: NaN,
          });
          dateAfterCurrentIndex = dateAfterCurrentIndex.add(1, 'day');
        }
      }
    },
    getAccountLabel(accountIndex, id) {
      const genericAccountLabel = `Account ${parseInt(accountIndex) + 1}`; // in case there is no matching account name
      return this.accountsMap[id]
        ? this.accountsMap[id].account_name
        : genericAccountLabel;
    },
    changeSelectedAccount(newAccount) {
      this.selectedAccount = this.accountsMap[newAccount];
    },
    selectMenuItem(menuItemSelected) {
      this.emitter.emit('daily-balance-graph-menu-event', menuItemSelected.key);
    },
    defaultAccountsAggregateData() {
      return {
        ...{
          data: [],
          ...constants.AGGREGATE_ACCOUNTS_OBJECT,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.graph-container {
  border: 1px solid $light-neutral-60;
  width: 100%;
  height: 400px;
}
.graph-chart {
  width: 100%;
  height: 300px;
  position: relative;
}

.account-selection-dropdown {
  min-width: 180px;
}
</style>
